import { useRef, useState, useCallback } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';

import {
  Popover,
  TouchDialog,
  Icon,
  registerIcon,
  useOuterEvent,
  Menu,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  calculateFontSize,
  isInstance,
  isTouchDevice
} from '@pega/cosmos-react-core';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';

import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(arrowMicroDownIcon);

const StyledFontSizeSelect = styled(Button)(({ theme }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    height: calc(3.5 * ${theme.base.spacing});
    padding: 0 calc(0.5 * ${theme.base.spacing});
    font-size: ${fontSizes.xxs};
    min-width: 6ch;

    span {
      white-space: nowrap;
    }
  `;
});

StyledFontSizeSelect.defaultProps = defaultThemeProp;

const StyledSelectMenu = styled.div(({ theme }) => {
  return css`
    min-width: 8ch;
    max-height: calc(30 * ${theme.base.spacing});
  `;
});

StyledSelectMenu.defaultProps = defaultThemeProp;

const FONT_SIZES = [
  { id: '8', label: '8px', value: '8px' },
  { id: '9', label: '9px', value: '9px' },
  { id: '10', label: '10px', value: '10px' },
  { id: '11', label: '11px', value: '11px' },
  { id: '12', label: '12px', value: '12px' },
  { id: '14', label: '14px', value: '14px' },
  { id: '16', label: '16px', value: '16px' },
  { id: '18', label: '18px', value: '18px' },
  { id: '20', label: '20px', value: '20px' },
  { id: '24', label: '24px', value: '24px' },
  { id: '28', label: '28px', value: '28px' },
  { id: '32', label: '32px', value: '32px' },
  { id: '36', label: '36px', value: '36px' },
  { id: '48', label: '48px', value: '48px' },
  { id: '72', label: '72px', value: '72px' }
];

interface FontSizeSelectProps {
  editor: TiptapEditor;
  'data-testid'?: string;
}

const FontSizeSelect = ({ editor, ...restProps }: FontSizeSelectProps) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const selecting = useRef(false);
  const openedByClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);

  useOuterEvent('mousedown', [buttonRef, popoverRef, menuRef, touchDialogRef], () => {
    if (open) {
      setOpen(false);
    }
  });

  const handleEscapeClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const onSizeSelect = (sizeId: string, e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const size = FONT_SIZES.find(s => s.id === sizeId);
    if (size) {
      editor.chain().focus().setFontSize(size.value).run();
    }

    if (!openedByClick.current) {
      buttonRef.current?.focus();
    }
    setOpen(false);
    selecting.current = true;
    setTimeout(() => {
      selecting.current = false;
      if (!editor.isFocused && openedByClick.current) {
        editor.commands.focus();
      }
    }, 0);
  };

  const getActiveSize = (): string => {
    const attrs = editor.getAttributes('textStyle');
    const { fontSize } = attrs;
    if (typeof fontSize !== 'string' || !fontSize) return '12px';
    const match = FONT_SIZES.find(s => s.value === fontSize);
    return match ? match.label : '12px';
  };

  const getActiveSizeId = (): string => {
    const attrs = editor.getAttributes('textStyle');
    const { fontSize } = attrs;
    if (typeof fontSize !== 'string' || !fontSize) return '12';
    const match = FONT_SIZES.find(s => s.value === fontSize);
    return match ? match.id : '12';
  };

  const menuContent = (
    <Menu
      items={FONT_SIZES.map(({ id, label }) => ({
        id,
        primary: label,
        selected: id === getActiveSizeId()
      }))}
      scrollAt={isTouchDevice ? FONT_SIZES.length : undefined}
      focusControlEl={isTouchDevice ? undefined : buttonRef.current ?? undefined}
      mode='single-select'
      ref={menuRef}
      onItemClick={onSizeSelect}
    />
  );

  return (
    <>
      <StyledFontSizeSelect
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          if (!open) {
            openedByClick.current = true;
          }
          setOpen(!open);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !selecting.current) {
            e.preventDefault();
            setOpen(true);
            openedByClick.current = false;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            selecting.current = false;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            setOpen(false);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          const { relatedTarget } = e;
          if (
            isInstance(relatedTarget, Node) &&
            !buttonRef.current?.contains(relatedTarget) &&
            !popoverRef.current?.contains(relatedTarget) &&
            !menuRef.current?.contains(relatedTarget) &&
            !touchDialogRef.current?.contains(relatedTarget)
          ) {
            setOpen(false);
          }
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-expanded={open}
        aria-label={`${t('rte_font_size')}. ${getActiveSize()} ${t('selected').toLocaleLowerCase()}`}
        aria-haspopup
      >
        <span>{getActiveSize()}</span>
        <Icon name='arrow-micro-down' />
      </StyledFontSizeSelect>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_font_size')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={t('rte_font_size')}
          onDismiss={() => setOpen(false)}
        >
          {menuContent}
        </TouchDialog>
      ) : (
        <Popover
          show={open}
          as={StyledSelectMenu}
          target={buttonRef.current}
          placement='bottom-start'
          ref={popoverRef}
        >
          {menuContent}
        </Popover>
      )}
    </>
  );
};

export default FontSizeSelect;
